/**
 * Trial utility functions for frontend
 * Checks trial limits and status
 */

// Trial limits (must match backend)
export const TRIAL_LIMITS = {
  guests: 2,
  vendors: 2,
  events: 1,
  tasks: 2,
  menu_items: 5,
  invitation_emails: 2,
  accommodation_emails: 2
};

/**
 * Check if a wedding is a trial wedding
 */
export const isTrialWedding = (wedding) => {
  return wedding?.is_trial === true;
};

/**
 * Check if trial has expired
 */
export const isTrialExpired = (wedding) => {
  return wedding?.trial_expired === true;
};

/**
 * Get trial days remaining
 */
export const getTrialDaysRemaining = (wedding) => {
  if (!wedding?.trial_days_remaining) return null;
  return wedding.trial_days_remaining;
};

/**
 * Check if trial is expiring soon (≤ 2 days)
 */
export const isTrialExpiringSoon = (wedding) => {
  const daysRemaining = getTrialDaysRemaining(wedding);
  return daysRemaining !== null && daysRemaining <= 2 && daysRemaining > 0;
};

/**
 * Check if user can add a resource based on trial limits
 * @param {Object} wedding - Wedding object with trial info
 * @param {string} resourceType - Type of resource (guests, vendors, events, tasks, menu_items)
 * @param {number} currentCount - Current count of that resource
 * @returns {Object} { canAdd: boolean, message: string, limit: number }
 */
export const canAddResource = (wedding, resourceType, currentCount) => {
  // If not a trial wedding, always allow
  if (!isTrialWedding(wedding)) {
    return { canAdd: true, message: '', limit: null };
  }

  // If trial expired, block
  if (isTrialExpired(wedding)) {
    return {
      canAdd: false,
      message: 'Trial expired. Upgrade to continue.',
      limit: null
    };
  }

  // Check limit
  const limit = TRIAL_LIMITS[resourceType];
  if (limit === undefined) {
    return { canAdd: true, message: '', limit: null }; // No limit for this type
  }

  if (currentCount >= limit) {
    return {
      canAdd: false,
      message: `Trial limit reached (${limit} ${resourceType}). Upgrade to continue.`,
      limit: limit
    };
  }

  return { canAdd: true, message: '', limit: limit };
};

/**
 * Get contextual blocked message for a resource type
 */
export const getBlockedMessage = (resourceType, limit) => {
  const messages = {
    guests: `You've added the maximum ${limit} guests allowed in trial.\nUpgrade to manage your full guest list.`,
    vendors: `You've added the maximum ${limit} vendors allowed in trial.\nUpgrade to manage your full vendor list.`,
    events: `Trial allows only ${limit} event.\nUpgrade to add your complete wedding timeline.`,
    tasks: `Trial allows only ${limit} tasks.\nUpgrade to manage your full checklist.`,
    menu_items: `You've added the maximum ${limit} menu items allowed in trial.\nUpgrade to plan your complete menu.`,
    invitation_emails: `Trial allows only ${limit} emails.\nUpgrade to send unlimited invitations and updates.`,
    accommodation_emails: `Trial allows only ${limit} emails.\nUpgrade to send unlimited accommodation details.`
  };
  
  return messages[resourceType] || `Trial limit reached. Upgrade to continue.`;
};

/**
 * Check if user can create a new wedding
 * @param {Array} weddings - Array of existing weddings
 * @param {Object} organization - Organization object
 * @returns {Object} { canCreate: boolean, message: string }
 */
export const canCreateWedding = (weddings, organization) => {
  // Check if trial wedding exists
  const trialWedding = weddings.find(w => w.is_trial);
  
  if (trialWedding) {
    if (trialWedding.trial_expired) {
      return {
        canCreate: false,
        message: 'Trial expired. Upgrade to continue.'
      };
    }
    return {
      canCreate: false,
      message: 'Trial wedding already exists. Upgrade to create more weddings.'
    };
  }

  // Check if has credits
  if (organization?.credits > 0) {
    return { canCreate: true, message: '' };
  }

  // Can start trial ONLY if:
  // - No trial wedding exists
  // - No credits
  // - No existing weddings (first time user)
  // - No trial_started_at (never started a trial)
  if (!trialWedding && 
      (!organization?.credits || organization.credits === 0) &&
      (!organization?.trial_started_at) &&
      weddings.length === 0) {
    return { canCreate: true, message: '' };
  }

  // Credits exhausted - need to upgrade
  if (organization?.credits === 0 && weddings.length > 0) {
    return {
      canCreate: false,
      message: 'All wedding credits used. Upgrade to create more weddings.'
    };
  }

  return {
    canCreate: false,
    message: 'No credits available. Upgrade to continue.'
  };
};
